import { ContextManager, createActions, createLoadable, Loadable } from 'dreamstate';

import { Maybe, Optional } from '@vimeo/core/types/generic';
import { Logger } from '@vimeo/logging';

import { ECredentialsType } from '../../../api/interaction';
import { IVimeoApiConnection } from '../../../types/api';
import {
  EComposerApplicationType,
  EComposerSessionType,
  TComposerSessionId,
} from '../../../types/composer_session';
import { ELiveErrorCode } from '../../../types/error';
import {
  IAuthorizeInteractionUserParams,
  IInteractionApplicationUser,
  IInteractionRoomInfo,
} from '../../../types/interaction_room';
import { IInteractionStoreInitialState } from '../../../types/store';
import { LiveError } from '../../../utils/error';
import { ELiveErrorCategory, withLiveErrorTracking } from '../../../utils/tracking';
import { loadSessionInfo } from './InteractionSessionManager.actions';
import {
  authorizeInteractionUser,
  logoutInteractionUser,
  resetAuthError,
  checkPasswordAuth,
} from './InteractionSessionManager.auth';

export interface IInteractionSessionConnections {
  auth: IVimeoApiConnection;
  credentialsCheck: IVimeoApiConnection;
}

export interface IInteractionSessionContext {
  interactionSessionActions: {
    authorizeInteractionUser: (params: IAuthorizeInteractionUserParams) => Promise<void>;
    logoutInteractionUser: () => Promise<void>;
    resetAuthError: () => void;
    checkPasswordAuth: (password: Maybe<string>, type?: Maybe<ECredentialsType>) => Promise<void>;
  };
  /**
   * Generic session fields.
   */
  sessionApplicationType: EComposerApplicationType;
  sessionType: EComposerSessionType;
  sessionId: TComposerSessionId;
  /**
   * Auth options disabled from usage in current room context.
   */
  roomDisabledAuthOptions: Array<string>;
  /**
   * Current interaction room hash for unlisted privacy.
   */
  roomUnlistedHash: Maybe<string>;
  /**
   * Current interaction room password for privacy.
   */
  roomPassword: Maybe<string>;
  /**
   * Current hashed interaction room password for privacy.
   */
  roomHashedPassword: Maybe<string>;
  /**
   * Current hashed interaction room password for privacy.
   */
  roomRegistrantUuid: Maybe<string>;
  /**
   * Injected jwt token for authorization of anonymous users on venues.
   */
  roomInjectedAuthToken: Maybe<string>;
  /**
   * Information about current interaction room.
   */
  roomInfo: Loadable<Optional<IInteractionRoomInfo>>;
  /**
   * Current interaction room auth details.
   */
  roomUser: Loadable<Optional<IInteractionApplicationUser>, LiveError>;
  /**
   * Whether auth attempt was performed.
   */
  isRoomUserAuthAttempted: boolean;
}

/**
 * Interaction room manager.
 * Manage current auth room information, login options and possible authorization.
 * Allows general session management with further propagation of firebase credentials for generic usage.
 */
export class InteractionSessionManager extends ContextManager<IInteractionSessionContext> {
  public override context: IInteractionSessionContext = {
    interactionSessionActions: createActions({
      authorizeInteractionUser: (params) => this.authorizeInteractionUser(params),
      logoutInteractionUser: () => this.logoutInteractionUser(),
      resetAuthError: () => this.resetAuthError(),
      checkPasswordAuth: (password: Maybe<string>, type?: Maybe<ECredentialsType>) =>
        this.checkPasswordAuth(password, type),
    }),
    sessionApplicationType: EComposerApplicationType.UNKNOWN,
    sessionType: EComposerSessionType.UNKNOWN,
    sessionId: -1,
    roomDisabledAuthOptions: [],
    roomPassword: null,
    roomHashedPassword: null,
    roomUnlistedHash: null,
    roomInjectedAuthToken: null,
    roomRegistrantUuid: null,
    roomInfo: createLoadable(null, true),
    roomUser: createLoadable(null),
    isRoomUserAuthAttempted: false,
  };

  public readonly log: Logger = new Logger('🌴ISM');

  public connections: Optional<IInteractionSessionConnections> = null;
  public isAuthAutoSignInDisabled: boolean = false;

  public constructor(initialState?: IInteractionStoreInitialState) {
    super();

    if (
      !initialState?.sessionId ||
      !initialState.sessionType ||
      !initialState.sessionApplicationType
    ) {
      throw new LiveError('Cannot initialize room session context.', {
        code: ELiveErrorCode.INITIALIZATION,
      });
    }

    this.context.sessionId = initialState.sessionId;
    this.context.sessionType = initialState.sessionType;
    this.context.sessionApplicationType = initialState.sessionApplicationType;

    this.isAuthAutoSignInDisabled = Boolean(initialState.interaction?.auth?.noAutoSignIn);
    this.context.roomRegistrantUuid = initialState?.interaction?.auth?.roomRegistrantUuid || null;
    this.context.roomUnlistedHash = initialState?.interaction?.auth?.roomUnlistedHash || null;
    this.context.roomPassword = initialState?.interaction?.auth?.roomPassword || null;
    this.context.roomHashedPassword = initialState?.interaction?.auth?.roomHashedPassword || null;
    this.context.roomInjectedAuthToken =
      initialState?.interaction?.auth?.roomInjectedAuthToken || null;
    this.context.roomDisabledAuthOptions =
      initialState?.interaction?.auth?.roomDisabledOptions || [];

    this.log.info(
      'Initializing room session manager for:',
      this.context.sessionType,
      this.context.sessionId,
    );
  }

  /**
   * Load live event if application is ready and global provision flow is started.
   */
  public override async onProvisionStarted(): Promise<void> {
    await this.loadSessionInfo();
  }

  /**
   * Load session ID room information and possible login options.
   */
  public async loadSessionInfo(): Promise<void> {
    return loadSessionInfo(this);
  }

  /**
   * Reset current auth error if it is set.
   */
  public resetAuthError(): void {
    return resetAuthError(this);
  }

  /**
   * Authorize in interaction tools application and fetch correct credentials.
   */
  public async authorizeInteractionUser(params: IAuthorizeInteractionUserParams): Promise<void> {
    return withLiveErrorTracking(() => authorizeInteractionUser(this, params), {
      category: ELiveErrorCategory.INTERACTION,
      method: 'authorizeInteractionUser',
    });
  }

  /**
   * Logout user and firebase connections for current page.
   */
  public async logoutInteractionUser(): Promise<void> {
    return withLiveErrorTracking(() => logoutInteractionUser(this), {
      category: ELiveErrorCategory.INTERACTION,
      method: 'logoutInteractionUser',
    });
  }

  /**
   * Assert room info is loaded and further actions can be done.
   */
  public assertIsInitialized(): asserts this is {
    connections: IInteractionSessionConnections;
    context: { roomInfo: Loadable<IInteractionRoomInfo> };
  } {
    if (!this.context.roomInfo.value || !this.connections) {
      throw new LiveError('Interaction session manager is not initialized.', {
        code: ELiveErrorCode.INITIALIZATION,
      });
    }
  }

  /**
   * Validate interaction room password and check auto auth options.
   * @param password - password for access to interaction room
   * @param type - password type for access to interaction room
   */
  public async checkPasswordAuth(
    password: Maybe<string>,
    type?: Maybe<ECredentialsType>,
  ): Promise<void> {
    return withLiveErrorTracking(() => checkPasswordAuth(this, password, type), {
      category: ELiveErrorCategory.INTERACTION,
      method: 'checkPasswordAuth',
    });
  }
}
